import {
  ElementKind,
  IProductElement,
  IElement,
  Baseline,
  ElementKindMap,
  OneOfChildElements,
  OneOfParentElements,
  OneOfElementListChildren,
  CreatedElement,
  VersionImports,
  DEFAULT_BASELINE_NAME,
} from '../models/project.model';
import {
  IFactoryElement,
  IFactoryProductElement,
  IFactoryVersion,
  OneOfChildFactoryElements,
  OneOfFactoryElements,
} from '../models/factory-element.model';
import { ArrayOrSingle } from '../models/type-helpers.model';
import { hasDefinedProperties, omitUndefined } from './object.helpers';
import { createExpression } from './expression-factory.helpers';
import { createElementProperties } from './element-property-factory.helpers';
import {
  createElementQuantityRecord,
  selectElementQuantity,
} from './element-quantity.helpers';
import { validateElement } from '../validation/project-validation';
import {
  flattenElements,
  getChildElements,
  getElementKind,
  isElement,
} from './recursive-element.helpers';
import { asArray } from './array.helpers';
import { createProposal } from './proposal.helpers';
import { IProposal } from '../models/proposals.model';
import { getUniqueProposalName } from './proposal-name.helpers';
import { sortInsert } from './sort.helpers';
import {
  createId,
  getChildRegenerateIdsOptions,
  getSourceRootId,
  RegenerateIds,
  shouldRegenerateIds,
} from './id.helpers';
import {
  remapActionsElementIds,
  mapDuplicateElementIdRecordKeys,
} from './actions/clean-actions.helpers';
import { restampDeterministicRecipeChildIdsInTree } from './recipe.helpers';

/**
 * Create an array of elements from a partial array of elements
 * @param factoryElements
 * @param regenerateId If true it will regerate id for all elements even if they already exist
 * @returns
 */
export const createElements = <T extends OneOfFactoryElements>(
  factoryElements: T[] = [],
  regenerateIds?: RegenerateIds,
): CreatedElement<T['kind']>[] =>
  factoryElements.map((e) => createElementFromPartial(e, regenerateIds));

/**
 * Make sure all child element is created with correct default values
 * @param defaults Partial with overrides of the default values
 * @param regenerateId If true it will regerate id for all elements even if id already exist
 * @returns
 */
const createChildElements = (
  defaults: Partial<IFactoryVersion | IFactoryElement>,
  regenerateIds?: RegenerateIds,
): OneOfChildElements[] =>
  createElements(
    defaults.elements,
    getChildRegenerateIdsOptions(regenerateIds),
  );

/**
 * Create a product element
 * @param defaults Partial with overrides of the default values
 * @param regenerateId how ids should be generated. Will regenerate by default.
 * @returns
 */
const createProductElement = (
  defaults: Partial<IFactoryProductElement>,
  regenerateIds?: RegenerateIds,
): IProductElement => {
  if (!defaults.product_id) {
    throw new Error('Product id is required');
  }

  return validateElement({
    kind: ElementKind.Product,
    product_id: '',
    generic_product_id: undefined,
    unit: 'kg',
    ...omitUndefined(defaults),
    count: createExpression(
      typeof defaults.count !== 'boolean' && defaults.count
        ? defaults.count
        : 1,
    ),
    id: createId(defaults, regenerateIds),
  });
};

const RECIPE_SCOPED_RESULTS_KEY_INFIX = ':r:';

/**
 * When duplicating with a pre-generated version id (`preserveRootElementIds`),
 * baseline/proposal `resultsRecord` rows may still be keyed by the source version id.
 * Recipe-scoped leftovers and orphan element ids must not be treated as that row —
 * only a single remaining extra key is unambiguous without {@link IRegenerateIdsOptions.sourceRootId}.
 */
const findLegacyVersionResultsRecordKey = (
  resultsRecord: Partial<Record<string, unknown>> | undefined,
  newVersionId: string,
  sourceElementIds: ReadonlySet<string>,
): string | undefined => {
  if (!resultsRecord) return undefined;
  const candidates = Object.keys(resultsRecord).filter(
    (key) =>
      key !== newVersionId &&
      !sourceElementIds.has(key) &&
      !key.includes(RECIPE_SCOPED_RESULTS_KEY_INFIX),
  );
  if (candidates.length !== 1) return undefined;
  return candidates[0];
};

/**
 * Resolves which resultsRecord key is the source version row when duplicating.
 * Prefers {@link IRegenerateIdsOptions.sourceRootId} so leftover keys cannot steal the bar.
 */
const resolveVersionResultsRecordSourceKey = (
  resultsRecord: Partial<Record<string, unknown>> | undefined,
  newVersionId: string,
  sourceElementIds: ReadonlySet<string>,
  regenerateIds?: RegenerateIds,
): string | undefined => {
  const sourceRootId = getSourceRootId(regenerateIds);
  if (sourceRootId && sourceRootId !== newVersionId) return sourceRootId;
  return findLegacyVersionResultsRecordKey(
    resultsRecord,
    newVersionId,
    sourceElementIds,
  );
};

/**
 * Remap stashed discard parent ids onto cloned element ids when a version is
 * duplicated (DEV-3357). The stashed element's own ids are regenerated on
 * restore, so only `parentId` (a reference into the cloned tree) needs mapping;
 * a stale parent still falls back to the version root on restore.
 */
const remapDiscardedRowParents = (
  imports: VersionImports | undefined,
  elementMap: Map<string, string> | undefined,
): VersionImports | undefined => {
  if (!imports || !elementMap?.size) return imports;
  const discardedRows: VersionImports['discardedRows'] = {};
  for (const [rowId, data] of Object.entries(imports.discardedRows)) {
    if (!data) continue;
    discardedRows[rowId] = data.stashed
      ? {
          ...data,
          stashed: {
            ...data.stashed,
            parentId:
              elementMap.get(data.stashed.parentId) ?? data.stashed.parentId,
          },
        }
      : data;
  }
  return { ...imports, discardedRows };
};

/**
 * Create an Baseline
 * @param defaults Partial with overrides of the default values
 * @param regenerateId how ids should be generated. Will regenerate by default.
 * @returns
 */
export const createBuildingVersion = (
  defaults: Partial<IFactoryVersion> = {},
  regenerateIds?: RegenerateIds,
): Baseline => {
  const { resultsRecord: previousVersionResultsRecord, ...defaultsRest } =
    defaults;

  const childRegenerateIds = getChildRegenerateIdsOptions(regenerateIds);
  const clonedElements = createChildElements(defaultsRest, regenerateIds);
  // Only re-stamp `:r:` ids when duplicating with regenerated child ids — not when loading
  // persisted rows or building legacy migration fixtures (plain recipe child ids).
  const elements = shouldRegenerateIds(childRegenerateIds)
    ? restampDeterministicRecipeChildIdsInTree(clonedElements)
    : clonedElements;
  const id = createId(defaultsRest, regenerateIds);
  const idMap = getElementIdMap(defaultsRest.elements, elements, regenerateIds);

  const sourceElementIds = new Set(
    flattenElements(
      ...(defaultsRest.elements ?? []).filter(
        (e): e is IElement => isElement(e) && hasDefinedProperties(e, 'id'),
      ),
    ).map((element) => element.id),
  );

  const legacyVersionResultsKey = resolveVersionResultsRecordSourceKey(
    previousVersionResultsRecord,
    id,
    sourceElementIds,
    regenerateIds,
  );

  // Add version id to element map when the root id changed or was pre-assigned on duplicate.
  if (idMap?.elementMap) {
    if (defaultsRest.id && defaultsRest.id !== id) {
      idMap.elementMap.set(defaultsRest.id, id);
    }
    if (legacyVersionResultsKey) {
      idMap.elementMap.set(legacyVersionResultsKey, id);
    }
  }

  const proposals = createProposals(
    defaultsRest.proposals,
    idMap,
    regenerateIds,
  );
  // When duplicating a version with regenerated ids, rewrite stored action changes so
  // their elementId references point at the new cloned ids. Skips when there is
  // no idMap (no regeneration happened) or when defaults carries no actions.
  const versionIdMap = new Map<string, string>();
  if (defaultsRest.id && defaultsRest.id !== id) {
    versionIdMap.set(defaultsRest.id, id);
  }
  if (legacyVersionResultsKey) {
    versionIdMap.set(legacyVersionResultsKey, id);
  }
  const { actions: remappedActions, proposals: remappedProposals } =
    remapActionsElementIds(
      defaultsRest.actions,
      proposals,
      idMap?.elementMap,
      versionIdMap.size ? versionIdMap : undefined,
      idMap?.sourceBaselineElementIds,
    );

  // Same key remap as {@link createProposal} for proposal rows; baseline lives on the version.
  const remappedVersionResultsRecord = idMap?.elementMap?.size
    ? mapDuplicateElementIdRecordKeys(
        previousVersionResultsRecord ?? {},
        idMap.elementMap,
        idMap.sourceBaselineElementIds,
      )
    : (previousVersionResultsRecord ?? {});

  const merged: Baseline = {
    kind: ElementKind.Version,
    name: DEFAULT_BASELINE_NAME,
    products: {},
    resultsRecord: {} as Baseline['resultsRecord'],
    ...omitUndefined(defaultsRest),
    // Properties are nested inside the version — regen flag must follow child semantics, not
    // the root flag, so `preserveRootElementIds` doesn't accidentally pin property ids too.
    properties: createElementProperties(
      defaultsRest.properties,
      getChildRegenerateIdsOptions(regenerateIds),
    ),
    proposals,
    ...(remappedActions !== undefined ? { actions: remappedActions } : {}),
    ...(remappedProposals !== undefined
      ? { proposals: remappedProposals }
      : {}),
    ...(defaultsRest.imports
      ? {
          imports: remapDiscardedRowParents(
            defaultsRest.imports,
            idMap?.elementMap,
          ),
        }
      : {}),
    id,
    elements,
  };

  // Baseline record must include the version id row (see {@link isValidVersion})
  return {
    ...merged,
    resultsRecord: {
      ...remappedVersionResultsRecord,
      [id]: remappedVersionResultsRecord[id] ?? {},
    },
  };
};

/**
 * Create an array of proposals. Omitted or `undefined` → empty list (V2 default); pass `[{}]` etc. to seed rows.
 * Will take care of making sure names are unique.
 * @param proposalDefaults An array of partials with overrides of the default values for each proposal
 * @param idMap
 * @param regenerateIds - Same semantics as version factory; forwarded to {@link createProposal} for each row’s `id`.
 * @returns
 */
export const createProposals = (
  proposalDefaults?: Partial<IProposal>[],
  idMap?: IElementVersionMap,
  regenerateIds: RegenerateIds = true,
): IProposal[] => {
  const rows = proposalDefaults ?? [];
  const names: Array<string | undefined> = [];
  return rows.map((p) => {
    // Make names unique
    const name = getUniqueProposalName(names, p.name);
    names.push(name);

    return createProposal({ ...p, name }, idMap, regenerateIds);
  });
};

/**
 * Map of duplicated element ids. Used when duplicating elements.
 */
export interface IElementVersionMap {
  elementMap: Map<IElement['id'], IElement['id']>;
  /** Element ids on the source version baseline tree (before duplicate remap). */
  sourceBaselineElementIds: ReadonlySet<string>;
}

/**
 * Will take the list of original elements and the newly created elements
 * and create a map of version ids and element ids (from old id to new id).
 * Used when duplicating elements.
 * @param prev The original elements
 * @param next The newly created elements
 * @returns
 */
export const getElementIdMap = (
  prev: OneOfChildFactoryElements[] = [],
  next: OneOfElementListChildren[] = [],
  regenerateIds?: RegenerateIds,
): IElementVersionMap | undefined => {
  // Child ids may still regenerate when the version root id is preserved (useAddVersion /
  // ElementAdd with preserveRootElementIds). Root regen options must not skip the map.
  if (!shouldRegenerateIds(getChildRegenerateIdsOptions(regenerateIds))) {
    return undefined;
  }
  const elementMap: Map<IElement['id'], IElement['id']> = new Map();

  const validRootElements = prev.filter(
    (e) => isElement(e) && hasDefinedProperties(e, 'id'),
  ) as IElement[];

  const prevElements = flattenElements(...validRootElements);
  const nextElements = flattenElements(...next);

  // If a version is duplicated length should be the same to
  if (prevElements.length !== nextElements.length) {
    return;
  }

  for (let i = 0; i < prevElements.length; i++) {
    const prevElement = prevElements[i];
    const nextElement = nextElements[i];

    // Remap if id is different
    if (prevElement && nextElement && prevElement.id !== nextElement.id) {
      elementMap.set(prevElement.id, nextElement.id);
    }
  }
  const sourceBaselineElementIds = new Set(
    prevElements.map((element) => element.id),
  );
  return elementMap.size ? { elementMap, sourceBaselineElementIds } : undefined;
};

/**
 * Create an element
 * @param defaults Partial with overrides of the default values
 * @param regenerateId how ids should be generated. Will regenerate by default.
 * @returns
 */
export const createElement = (
  defaults: Partial<IFactoryElement> = {},
  regenerateId?: RegenerateIds,
): IElement => {
  // Omit undefined values so they don't override values by mistake
  defaults = omitUndefined(defaults);

  // Properties and quantity items live inside the element and must follow the same regen
  // rule as nested children — never the root rule. Using the root regen options here would
  // wrongly preserve property/quantity ids when the caller asked for `preserveRootElementIds`,
  // which is why we collapse to the boolean using the child-scoped options.
  const regenerateBool = shouldRegenerateIds(
    getChildRegenerateIdsOptions(regenerateId),
  );
  const quantity = createElementQuantityRecord(
    defaults.quantity,
    regenerateBool,
  );

  // Default value for count, provide only if unit is passed
  const element: IElement = selectElementQuantity(
    {
      kind: ElementKind.Element,
      name: '',
      ...defaults,
      id: createId(defaults, regenerateId),
      quantity,
      properties: createElementProperties(defaults.properties, regenerateBool),
      elements: createChildElements(defaults, regenerateId),
    },
    'auto',
  );

  // Make sure element is valid before returning
  return element;
};

/**
 * Create a new element of a specific kind.
 * Can also be used to shallow clone an element.
 * @param kind Type of element which should be created
 * @param defaults A partial with overides of the default values
 * @param regenerateId If true this will always return element with a new id
 * @returns
 */
export function createElementOfType<T extends keyof ElementKindMap>(
  kind: T,
  defaults: Partial<FactoryKindMap[T]> = {},
  regenerateIds?: RegenerateIds,
): CreatedElement<T> {
  switch (kind) {
    case ElementKind.Version: {
      return createBuildingVersion(
        defaults as Partial<IFactoryVersion>,
        regenerateIds,
      ) as CreatedElement<T>;
    }
    case ElementKind.Element: {
      return createElement(
        defaults as Partial<IFactoryElement>,
        regenerateIds,
      ) as CreatedElement<T>;
    }
    case ElementKind.Product: {
      return createProductElement(
        defaults as Partial<IFactoryProductElement>,
        regenerateIds,
      ) as CreatedElement<T>;
    }
  }
  throw new Error(`Can't create element of type ${kind}`);
}

/**
 * Create a new element of a specific kind.
 * Can also be used to shallow clone an element.
 * @param initialValues A partial with overides of the default values. Need to contain kind
 * @param regenerateId If true this will always return element with a new id
 * @returns
 */
export function createElementFromPartial<T extends OneOfFactoryElements>(
  initialValues: T,
  regenerateId?: RegenerateIds,
): CreatedElement<T['kind']> {
  return createElementOfType(
    initialValues.kind,
    initialValues,
    regenerateId,
  ) as CreatedElement<T['kind']>;
}

/**
 * Add element to parent. TODO: Rewrite addElements in project_helpers.ts to support this
 * @param parent
 * @param initialValues One or more elements to be added
 * @returns The modified parent
 */
export const addElementsToParent = <R extends OneOfParentElements>(
  parent: OneOfParentElements,
  initialValues: ArrayOrSingle<OneOfChildFactoryElements>,
  regenerateId = true,
): R => {
  const originalId = 'id' in initialValues ? initialValues.id : undefined;
  const elements = getChildElements(parent);
  const original = originalId
    ? elements.find((e) => e.id === originalId)
    : undefined;
  const newElements = createElements(asArray(initialValues), regenerateId);
  return {
    ...parent,
    // elements: [...elements, ...newElements],

    elements: sortInsert<OneOfElementListChildren>(
      'after',
      elements,
      original,
      ...newElements,
    ),
  } as R;
};

/**
 * Use FactoryKindMap to get correct type of elements.
 * Maps element kind to the corresponding factory type.
 */
type FactoryKindMap = {
  [ElementKind.Product]: IFactoryProductElement;
  [ElementKind.Element]: IFactoryElement;
  [ElementKind.Version]: IFactoryVersion;
};

/**
 * Check if element is a factory product element
 * @param element
 * @returns
 */
export const isFactoryProductElement = (
  element?: OneOfFactoryElements,
): element is IFactoryProductElement => {
  return getElementKind(element) === ElementKind.Product;
};

/**
 * Check if element is a factory version
 * @param element
 * @returns
 */
export const isFactoryVersion = (
  element?: OneOfFactoryElements,
): element is IFactoryVersion => {
  return getElementKind(element) === ElementKind.Version;
};

/**
 * Check if element is a factory element
 * @param element
 * @returns
 */
export const isFactoryElement = (
  element?: OneOfFactoryElements,
): element is IFactoryElement => {
  return getElementKind(element) === ElementKind.Element;
};

/**
 * Check if element is a factory version or a factory element (can have children)
 * @param element
 * @returns
 */
export const isOneOfParentFactoryElements = (
  element?: OneOfFactoryElements,
): element is IFactoryVersion | IFactoryElement => {
  return isFactoryVersion(element) || isFactoryElement(element);
};
